import { useLazyQuery } from '@apollo/client'
import { notNullOrUndefined, Nullable } from '@dai/common'
import dayjs from 'dayjs'
import { isEmpty, isNil } from 'lodash'
import { useEffect } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  DisplaySnapshotWithImageBoxesFragment,
  GET_RESTOCK_GROUP,
  GetRestockGroupQuery,
  GetRestockGroupQueryVariables,
  RestockGroupFragment,
  SnapshotType,
} from '@/graphql'
import { ProductHelpers } from '@/utils/products.helpers'

interface SnapshotStats {
  emptyFacings: number
  identifiedSkus: number
  totalFacings: number
}

interface RestockStats {
  before: SnapshotStats
  after: SnapshotStats
  deltas: {
    emptyFacings: number
    identifiedSkus: number
    totalFacings: number
  }
}

export const getSnapshotByType = (
  snapshots: DisplaySnapshotWithImageBoxesFragment[] | null,
  type: SnapshotType,
) => {
  return snapshots?.find(snapshot => snapshot.type === type.toLowerCase())
}

export const getSnapshotStats = (
  snapshot: DisplaySnapshotWithImageBoxesFragment | undefined,
): SnapshotStats => {
  if (!snapshot) return { emptyFacings: 0, identifiedSkus: 0, totalFacings: 0 }

  const emptyFacings = snapshot.emptyFacings?.length || 0
  const boundingBoxes = ProductHelpers.filterRestockBoxes(
    snapshot.images[0]?.boxSet?.boundingBoxes || [],
  )
  const products = ProductHelpers.filterProductBoxes(
    boundingBoxes.filter(({ classifiedLabel }) => !isNil(classifiedLabel)),
  )
  const identifiedSkus = new Set(
    ProductHelpers.filterIdentifiedProductBoxes(products),
  ).size
  const totalFacings = products.length

  return { emptyFacings, identifiedSkus, totalFacings }
}

export const calculateRestockStats = (
  beforeSnapshot: DisplaySnapshotWithImageBoxesFragment | undefined,
  afterSnapshot: DisplaySnapshotWithImageBoxesFragment | undefined,
): RestockStats => {
  const before = getSnapshotStats(beforeSnapshot)
  const after = getSnapshotStats(afterSnapshot)

  return {
    before,
    after,
    deltas: {
      emptyFacings: after.emptyFacings - before.emptyFacings,
      identifiedSkus: after.identifiedSkus - before.identifiedSkus,
      totalFacings: after.totalFacings - before.totalFacings,
    },
  }
}

export const formatStatChange = (before: number, after: number) => {
  if (before === 0 && after === 0) return '0'
  return `${before} → ${after}`
}

export const formatDelta = (delta: number) => {
  if (delta === 0) return ''
  return delta > 0 ? ` (+${delta})` : ` (${delta})`
}

export const useRestockGroupData = (restockGroupId: string | undefined) => {
  const [getRestockGroup, restockGroupData] = useLazyQuery<
    GetRestockGroupQuery,
    GetRestockGroupQueryVariables
  >(GET_RESTOCK_GROUP, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    if (restockGroupId) {
      getRestockGroup({
        variables: { id: restockGroupId },
      })
    }
  }, [restockGroupId, getRestockGroup])

  useRefresh(() => {
    if (!isEmpty(restockGroupData.variables)) {
      restockGroupData.refetch()
    }
  })

  return restockGroupData
}

export const useTimingData = (restockGroup: Nullable<RestockGroupFragment>) => {
  const completedTime = dayjs(restockGroup?.created)
  const timeString = restockGroup?.actionComplete?.action.dueDateTime
  const dateString = restockGroup?.actionComplete?.action.dueDate

  const dueDate =
    !!dateString && !!timeString ? dayjs(`${dateString} ${timeString}`) : null

  const isOnTime = !!dueDate && dueDate.isSameOrAfter(completedTime)

  const lateAmount = dueDate
    ? dayjs.duration(dayjs(dueDate).diff(dayjs(completedTime))).humanize()
    : null

  const snapshotTimes: dayjs.Dayjs[] =
    restockGroup?.restocks
      .flatMap(restock =>
        restock.displaySnapshots?.map(snapshot => snapshot.captureTime),
      )
      .filter(notNullOrUndefined)
      .map(time => dayjs(time)) || []

  const minTime = snapshotTimes.length
    ? snapshotTimes.reduce((a, b) => (a.isBefore(b) ? a : b))
    : null
  const maxTime = snapshotTimes.length
    ? snapshotTimes.reduce((a, b) => (a.isAfter(b) ? a : b))
    : null
  const timeToComplete = dayjs
    .duration(dayjs(maxTime).diff(dayjs(minTime)))
    .humanize()

  return {
    completedTime,
    dueDate,
    isOnTime,
    lateAmount,
    timeToComplete,
  }
}
